import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { TreePine, Waves, Fish, Footprints, Snowflake, Mountain, MapPin, ExternalLink, Download, Ruler, Bell, Flag, ChevronRight } from "lucide-react";
import { OrnamentDivider, VineOrnament } from "@/components/Ornaments";
import { MapLink } from "@/components/MapLink";
import RegionMap from "@/components/RegionMap";
import { TRAIL_ROUTES, SKOTER_ROUTES } from "@/data/trailRoutes";
import { TRAILS_BY_AREA, trailSlug, PDF_KARTA_BACKE, PDF_KARTA_SILSJO, PDF_STIGBESKR, SOURCE_URL } from "@/data/trails";
import { usePageMeta } from "@/utils/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INFO_SECTIONS = [
  {
    key: "badplatser",
    icon: Waves,
    title: "Badplatser och sjöar",
    body: [
      "Stenhammaren söder om Backe är bygdens badplats sedan generationer – fin sand, brygga och hopptorn vid Fjällsjöälvens lugna parti. Vattnet är kallt in i juli, ljummet i augusti.",
      "Backesjön och Rossöns strandparti har också lugna vikar för den som vill bada enskilt. Kilevippen vid Backe erbjuder utsikt över vattnet och lekmöjligheter för barnen.",
    ],
  },
  {
    key: "fiske",
    icon: Fish,
    title: "Fiske i älv och sjöar",
    body: [
      "Fjällsjöälven och dess sjöar har öring, harr, abborre och gädda. Två aktiva fiskevårdsområdesföreningar säljer fiskekort: Rossöns FVO och Fjällsjö FVOF.",
      "Kort köps lokalt via iFiske eller hos respektive förening. Regler för båtfiske, nätfiske och vinterfiske finns på respektive hemsida.",
    ],
    links: [{ namn: "iFiske – Rossöns & Fjällsjö FVOF", url: "https://www.ifiske.se/" }],
  },
  {
    key: "evenemang",
    icon: Mountain,
    title: "Friluftsevenemang under året",
    body: [
      "Fjällsjömarknaden vid Stenhammaren i augusti är årets största händelse – hela bygden samlas vid älven. 2026 firas 80-årsjubileum med Sverigecupen i Watercross.",
      "Vintern bjuder på Watercross på isen, påskskidor vid Backes anläggning och midsommarfirande på Bodums Hembygdsgård.",
    ],
  },
];

const ROSSON_PLATSER = [
  { namn: "Bollsbergets naturreservat", beskr: "Dramatiska berg, gammal tallskog, små dalsjöar. Ca 3–4 km väster om Rossön. Oplogat vintertid." },
  { namn: "Rossöns Golfklubb", beskr: "9-håls bana vid Hotingsvägen/Näset, öppen säsongsvis. Bredvid: skidstadion för längdåkning." },
  { namn: "Rossöns IF skidstadion", beskr: "Preparerade längdspår, belyst kortspår. Samma område som golfklubben." },
  { namn: "Rossöns Stenåldersmuseum", beskr: "6 000 år av fynd från 120 boplatser längs älven. Öhnvägen 5." },
  { namn: "Rossöns Camping", beskr: "Sjönära camping med badplats och båtuthyrning." },
  { namn: "Finlanda flygfält", beskr: "Gräsbana med två hangarer – fortfarande aktivt. Ca 2 km söder om Rossön. Starka promenadstigar runt fältet." },
  { namn: "Rossö FVO", beskr: "Rossöns fiskevårdsområde – öring, harr, abborre. Kort via iFiske." },
];

export default function FriluftslivSida() {
  const { t } = useTranslation();
  const [platser, setPlatser] = useState([]);
  const [justnu, setJustnu] = useState([]);

  usePageMeta({
    title: "Friluftsliv – vidalv.live",
    description: "Vandringsleder, fiskeplatser, badställen och skoterleder kring Backe, Rossön och Fjällsjöälven. Kartor, längder och tips från bygden.",
    path: "/friluftsliv",
  });

  useEffect(() => {
    axios.get(`${API}/karttipps`).then(r => {
      const filtered = (r.data || []).filter(p =>
        ["Friluftsliv", "Natur", "Sevärdhet", "Kulturminne", "Vandring"].includes(p.kategori)
      );
      setPlatser(filtered);
    }).catch(() => {});
    axios.get(`${API}/anslag?rubrik=Friluftsliv`).then(r => setJustnu(r.data || [])).catch(() => {});
  }, []);

  // Mini-map markers: only Vandring start-points
  const vandringMarkers = useMemo(() =>
    platser
      .filter(p => p.kategori === "Vandring" && typeof p.lat === "number")
      .map((p, i) => ({
        id: p.id,
        lat: p.lat,
        lng: p.lng,
        cat: "Friluftsliv",
        number: i + 1,
        hoverLabel: `${p.namn}${p.langd_km ? ` (${p.langd_km} km)` : ""}`,
        popupHtml: `<h4>${i + 1}. ${p.namn}</h4><p style="font-size:11px;color:#64594a">${p.langd_km || ""} km · ${p.omrade || ""}</p><p style="font-size:11px">${(p.beskrivning || "").slice(0, 120)}...</p>`,
      })),
    [platser]
  );

  // Kombinera led-nätet: vandring (grön streckad) + skoter (blå streckad)
  const allLines = useMemo(() => [...TRAIL_ROUTES, ...SKOTER_ROUTES], []);

  return (
    <div data-testid="friluftsliv-page" className="relative max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <VineOrnament className="hidden lg:block absolute top-10 -left-20 w-16 h-48 text-gold-deep opacity-35" />
      <VineOrnament className="hidden lg:block absolute top-10 -right-20 w-16 h-48 text-gold-deep opacity-35" flip />

      <p className="font-body text-[11px] font-semibold text-bark/50 uppercase tracking-[0.22em] mb-1.5">
        {t("nav.friluftsliv")}
      </p>
      <h1 className="font-fraktur-smooth text-xl sm:text-2xl text-falu tracking-wide mb-3 leading-[1.05]">
        {t("friluftsliv.title")}
      </h1>
      <OrnamentDivider className="text-gold-deep opacity-50 my-3" />
      <p className="text-base text-bark mb-10 max-w-2xl leading-relaxed">
        {t("friluftsliv.intro")}
      </p>

      {/* Just nu-flöde (tomt som default tills moderatorerna fyller) */}
      {justnu.length > 0 && (
        <section data-testid="friluftsliv-justnu" className="mb-10 bg-pine/8 border-l-4 border-pine rounded-sm p-5 sm:p-6">
          <div className="flex items-center gap-2 mb-3">
            <Bell size={16} className="text-pine" />
            <p className="font-body text-[11px] font-semibold text-pine uppercase tracking-[0.22em]">Just nu i bygden</p>
          </div>
          <div className="flex flex-col gap-3">
            {justnu.slice(0, 4).map(a => (
              <Link key={a.id} to={`/anslag/${a.id}`} data-testid={`justnu-${a.id}`}
                className="block bg-parchment border border-warm-border rounded-sm p-3 hover:border-pine/50 transition-colors no-underline">
                <p className="font-heading text-base font-semibold text-ink mb-0.5">{a.titel}</p>
                {a.beskrivning && <p className="text-sm text-bark/75 line-clamp-2">{a.beskrivning}</p>}
                <p className="text-[11px] text-bark/45 mt-1">
                  {a.ort && `${a.ort} · `}{a.publiceringsdatum ? new Date(a.publiceringsdatum).toLocaleDateString("sv-SE") : ""}
                </p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Dedikerad mini-karta med led-nät */}
      <section data-testid="friluftsliv-minikarta" className="mb-10">
        <div className="flex items-end justify-between gap-3 mb-3">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <MapPin size={18} className="text-pine" />
              <h2 className="font-fraktur-smooth text-xl sm:text-2xl text-falu tracking-wide leading-tight">
                Led-kartan
              </h2>
            </div>
            <p className="text-sm text-bark/70">Vandringsleder och skoterleder tillsammans. Streckade linjer = ungefärlig sträckning.</p>
          </div>
          <div className="flex items-center gap-4 shrink-0 text-[11px] font-body">
            <span className="inline-flex items-center gap-1.5 text-pine">
              <span className="inline-block w-6 h-[2px] bg-pine" style={{ borderTop: "2px dashed #3F6B4E", background: "transparent" }} />
              Vandring
            </span>
            <span className="inline-flex items-center gap-1.5 text-[#4a6075]">
              <span className="inline-block w-6 h-[2px]" style={{ borderTop: "2px dashed #4a6075" }} />
              Skoter
            </span>
          </div>
        </div>
        <div data-testid="friluftsliv-minikarta-container" className="relative w-full h-[440px] rounded-sm overflow-hidden border border-warm-border bg-parchment">
          <RegionMap
            variant="page"
            center={[16.390, 63.855]}
            zoom={10.2}
            markers={vandringMarkers}
            lines={allLines}
            className="absolute inset-0"
          />
        </div>
        <p className="text-[11px] text-bark/55 italic mt-2">
          Källa ledbeskrivningar: <a href={SOURCE_URL} target="_blank" rel="noopener noreferrer" className="text-pine hover:text-pine-hover">backe.nu</a>. Skoterled: Skotersällskapet Tre Kommuner (approx från OSM).
          <br />
          <span className="text-bark/45">Klicka på en led nedan för dedikerad karta och detaljer.</span>
        </p>
      </section>

      {/* Vandringsleder */}
      <section data-testid="friluftsliv-vandring" className="mb-12">
        <div className="flex items-center gap-2 mb-1.5">
          <Footprints size={22} className="text-pine" />
          <h2 className="font-fraktur-smooth text-xl sm:text-2xl text-falu tracking-wide leading-tight">
            Vandringsleder
          </h2>
        </div>
        <p className="text-base text-bark mb-4 max-w-2xl leading-relaxed">
          Sex mil stigar inom en mils radie från Backe, Jansjö och Silsjönäs – berg, sjöar, fäbodar, hällmålningar och sandrevlar från istiden.
        </p>
        <div className="flex flex-wrap gap-2 mb-6">
          <a href={PDF_KARTA_BACKE} target="_blank" rel="noopener noreferrer" data-testid="pdf-karta-backe"
            className="inline-flex items-center gap-1.5 text-xs font-body font-semibold text-parchment bg-pine hover:bg-pine-hover px-3 py-2 rounded-sm no-underline transition-colors">
            <Download size={13} /> Karta Backe + Jansjö (PDF)
          </a>
          <a href={PDF_KARTA_SILSJO} target="_blank" rel="noopener noreferrer" data-testid="pdf-karta-silsjo"
            className="inline-flex items-center gap-1.5 text-xs font-body font-semibold text-pine bg-parchment border border-pine/40 hover:bg-pine/5 px-3 py-2 rounded-sm no-underline transition-colors">
            <Download size={13} /> Karta Silsjönäs
          </a>
          <a href={PDF_STIGBESKR} target="_blank" rel="noopener noreferrer" data-testid="pdf-stigbeskr"
            className="inline-flex items-center gap-1.5 text-xs font-body font-semibold text-pine bg-parchment border border-pine/40 hover:bg-pine/5 px-3 py-2 rounded-sm no-underline transition-colors">
            <Download size={13} /> Stigbeskrivningar (PDF)
          </a>
        </div>

        {Object.entries(TRAILS_BY_AREA).map(([omr, leder]) => {
          const omradeSlug =
            omr === "Silsjönäs"
              ? "silsjonas"
              : omr === "Backe" || omr === "Jansjö"
              ? "fjallsjo-vandringseldorado"
              : null;
          return (
          <div key={omr} data-testid={`trail-group-${omr}`} className="mb-6">
            <div className="flex items-baseline justify-between gap-2 mb-3 border-b border-warm-border/60 pb-1">
              <h3 className="font-heading text-lg sm:text-xl font-semibold text-ink">
                Stigar i {omr}
              </h3>
              {omradeSlug && (
                <Link
                  to={`/friluftsliv/omrade/${omradeSlug}`}
                  data-testid={`omradesoversikt-link-${omradeSlug}`}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-pine hover:text-pine-hover no-underline transition-colors"
                >
                  Områdesöversikt <ChevronRight size={12} />
                </Link>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {leder.map(led => (
                <Link
                  key={led.namn}
                  to={`/friluftsliv/led/${trailSlug(led.namn)}`}
                  data-testid={`trail-${trailSlug(led.namn)}`}
                  className={`group relative bg-parchment border border-warm-border rounded-sm p-4 no-underline block hover:border-pine/60 hover:shadow-sm transition-all ${led.hero ? "sm:col-span-2 border-gold/50" : ""} ${led.status === "stängd" ? "opacity-70" : ""}`}>
                  <div className="flex items-start justify-between gap-3 mb-1">
                    <h4 className="font-heading text-base font-semibold text-ink leading-snug group-hover:text-pine transition-colors">{led.namn}</h4>
                    <span className="shrink-0 inline-flex items-center gap-1 text-[11px] font-body font-semibold text-pine bg-pine/10 px-2 py-0.5 rounded-sm">
                      <Ruler size={10} /> {led.km} km
                    </span>
                  </div>
                  {led.status === "stängd" && (
                    <p className="text-[11px] font-semibold text-falu uppercase tracking-wider mb-1">⚠ Stigen är stängd</p>
                  )}
                  <p className="text-sm text-bark leading-relaxed line-clamp-3">{led.text}</p>
                  <p className="mt-2 inline-flex items-center gap-1 text-[11px] font-body font-semibold text-pine group-hover:text-pine-hover">
                    <MapPin size={11} /> Visa karta &amp; detaljer <ChevronRight size={11} />
                  </p>
                </Link>
              ))}
            </div>
          </div>
          );
        })}

        <p className="text-[11px] text-bark/55 italic mt-2">
          Källa: beskrivningar från <a href={SOURCE_URL} target="_blank" rel="noopener noreferrer" className="text-pine hover:text-pine-hover">backe.nu/vandringseldorado</a> (Fjällsjö Framtid). Klicka på en led för karta och detaljer.
        </p>
      </section>

      {/* Rossön-sektion */}
      <section data-testid="friluftsliv-rosson" className="mb-12 bg-parchment border border-warm-border rounded-sm p-6 sm:p-8 jugendstil-card">
        <div className="flex items-center gap-2 mb-3">
          <Flag size={20} className="text-pine" />
          <h2 className="font-fraktur-smooth text-xl sm:text-2xl text-falu tracking-wide leading-tight">Friluftsliv i Rossön</h2>
        </div>
        <p className="text-base text-bark mb-4 leading-relaxed">
          Rossön har inte Backes PDF-dokumenterade led-nät, men väl en egen rikedom: naturreservat, golfbana, skidstadion, stenåldersmuseum och fiskevatten. Här är platserna folk i bygden rekommenderar.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {ROSSON_PLATSER.map(p => (
            <div key={p.namn} data-testid={`rosson-${p.namn.replace(/\s/g, '-').toLowerCase()}`}
              className="bg-bone border border-warm-border rounded-sm p-3">
              <p className="font-heading text-sm font-semibold text-ink mb-1">{p.namn}</p>
              <p className="text-[13px] text-bark/80 leading-relaxed mb-1.5">{p.beskr}</p>
              <MapLink name={p.namn} />
            </div>
          ))}
        </div>
        <p className="text-[11px] text-bark/55 italic mt-4">
          Har du fler förslag på Rossön-stigar och friluftsplatser? <Link to="/skicka-anslag" className="text-pine hover:text-pine-hover">Skicka in</Link> så lyfter vi dem.
        </p>
      </section>

      {/* Snöskoter */}
      <section data-testid="friluftsliv-skoter" className="mb-12 bg-parchment border border-warm-border rounded-sm p-6 sm:p-8 jugendstil-card">
        <div className="flex items-center gap-2 mb-3">
          <Snowflake size={20} className="text-[#4a6075]" />
          <h2 className="font-fraktur-smooth text-xl sm:text-2xl text-falu tracking-wide leading-tight">Snöskoter</h2>
        </div>
        <div className="flex flex-col gap-3 text-base text-ink/80 leading-relaxed">
          <p>
            <strong>Skotersällskapet Tre Kommuner</strong> (Strömsund, Sollefteå, Junsele) sköter ett samman­länkat led-nät som binder ihop Backe, Rossön, Junsele och Näsåker. Huvudleden följer Fjällsjöälvens dalgång och grenar ut mot fjällen i väster.
          </p>
          <p>
            <strong>Led-kort</strong> säljs via klubben eller direkt på bensinstationerna. Gratis för medlemmar. Alla som kör skoter på det skötta nätet förväntas bidra.
          </p>
          <ul className="list-disc pl-5 text-sm marker:text-pine">
            <li>Säsong: december (när snön bär) till april/maj.</li>
            <li>Tankmöjligheter: Mack Backe och Mack Rossön.</li>
            <li>Shelters: vid Gammrågan (Svarttjärnskojan), Lidtjärn och längs huvudleden mot Junsele.</li>
            <li>Viktigt: respektera renskötsel-områden och privat mark. Inga skoter-avstickare mellan 20 april och 1 december.</li>
          </ul>
          <p className="text-sm text-bark/70">
            Visa huvudleden på <Link to="#friluftsliv-minikarta" onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid=friluftsliv-minikarta]')?.scrollIntoView({ behavior: "smooth" }); }} data-testid="show-skoter-map" className="text-pine hover:text-pine-hover underline">led-kartan ovan</Link> (blå streckad linje).
          </p>
          <div className="flex flex-wrap gap-2 mt-1">
            <a href="https://www.skoterleder.org" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-body font-semibold text-[#4a6075] bg-parchment border border-[#4a6075]/40 hover:bg-[#4a6075]/5 px-3 py-2 rounded-sm no-underline">
              <ExternalLink size={12} /> skoterleder.org (nationell ledkarta)
            </a>
          </div>
        </div>
      </section>

      {/* Övriga friluftssektioner */}
      <div className="flex flex-col gap-6">
        {INFO_SECTIONS.map(sec => (
          <section key={sec.key} data-testid={`friluftsliv-${sec.key}`}
            className="relative bg-parchment border border-warm-border rounded-sm p-6 sm:p-8 jugendstil-card">
            <div className="flex items-center gap-3 mb-4">
              <sec.icon size={20} className="text-pine" />
              <h2 className="font-heading text-xl sm:text-2xl font-semibold text-ink leading-tight">
                <span className="jugendstil-accent">{sec.title}</span>
              </h2>
            </div>
            <div className="flex flex-col gap-3 text-base text-ink/80 leading-relaxed">
              {sec.body.map((p, i) => <p key={i}>{p}</p>)}
              {sec.links && (
                <div className="flex flex-wrap gap-3 mt-1">
                  {sec.links.map(l => (
                    <a key={l.namn} href={l.url} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-falu hover:text-falu-hover">
                      {l.namn} <ExternalLink size={12} />
                    </a>
                  ))}
                </div>
              )}
            </div>
          </section>
        ))}
      </div>

      {/* Kulturhistorisk avstickare */}
      <section data-testid="friluftsliv-kultur" className="mt-10 border-t border-warm-border pt-6">
        <p className="font-body text-[11px] font-semibold text-bark/50 uppercase tracking-[0.22em] mb-1.5">Kulturhistoria vid stigen</p>
        <Link to="/upptack/mystiska-korset" className="block group no-underline">
          <h3 className="font-heading text-xl font-semibold text-ink group-hover:text-falu transition-colors mb-1">Mystiska korset – hällmålning längs Djuptjärnsstigen</h3>
          <p className="text-sm text-bark leading-relaxed">1 000–2 000 år gammal ockramålning på ett jätteflyttblock vid Jansjöbodarna. Läs berättelsen →</p>
        </Link>
      </section>
    </div>
  );
}
